/* eslint-disable */
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { currencyLabel } from "@/lib/currency";
import { toast } from "sonner";
import { Plus, TrendingUp, TrendingDown, X, Loader2, Wallet } from "lucide-react";

export default function JobsList() {
  const nav = useNavigate();
  const { t, i18n } = useTranslation();
  const cur = currencyLabel(i18n.language);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ title: "", client_name: "", value: "" });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/jobs");
      setJobs(r.data);
    } catch (e) {
      toast.error(t("jobs.loadError"));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const now = new Date();
  const ym = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  const monthJobs = jobs.filter((j) => (j.created_at || "").slice(0, 7) === ym);
  const sum = (arr, f) => arr.reduce((a, j) => a + (f(j) || 0), 0);
  const income = sum(monthJobs, (j) => j.totals?.value);
  const expenses = sum(monthJobs, (j) => j.totals?.total_expenses);
  const profit = income - expenses;
  const activeCount = jobs.filter((j) => j.status !== "completed").length;

  const create = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return toast.error(t("jobs.addTitleError"));
    setSaving(true);
    try {
      const r = await api.post("/jobs", { title: form.title, client_name: form.client_name, value: parseFloat(form.value) || 0 });
      toast.success(t("jobs.created"));
      nav(`/app/lucrari/${r.data.id}`);
    } catch (e) {
      toast.error(t("jobs.createError"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-6 lg:p-10 max-w-6xl mx-auto" data-testid="jobs-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="overline">{t("jobs.overline")}</div>
          <h1 className="font-display text-3xl lg:text-4xl font-extrabold tracking-tight mt-2">{t("jobs.title")}</h1>
          <p className="text-sm text-[#94A3B8] mt-2">{t("jobs.subtitle")}</p>
        </div>
        <button onClick={() => setShowNew(true)} className="btn-primary flex items-center gap-2" data-testid="new-job-btn">
          <Plus size={18} /> {t("jobs.newJob")}
        </button>
      </div>

      {!loading && jobs.length > 0 && (
        <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="jobs-dashboard">
          <div className="card-flat p-4">
            <div className="text-[10px] uppercase text-[#94A3B8]">{t("jobs.incomeMonth")}</div>
            <div className="font-display text-2xl font-extrabold mt-1">{income.toFixed(0)} <span className="text-sm text-[#94A3B8]">{cur}</span></div>
          </div>
          <div className="card-flat p-4">
            <div className="text-[10px] uppercase text-[#94A3B8]">{t("jobs.expensesMonth")}</div>
            <div className="font-display text-2xl font-extrabold mt-1">{expenses.toFixed(0)} <span className="text-sm text-[#94A3B8]">{cur}</span></div>
          </div>
          <div className={`p-4 text-white ${profit >= 0 ? "bg-[#22C55E]" : "bg-[#EF4444]"}`} data-testid="dashboard-profit">
            <div className="text-[10px] uppercase opacity-80">{t("jobs.profitMonth")}</div>
            <div className="font-display text-2xl font-extrabold mt-1">{profit.toFixed(0)} <span className="text-sm opacity-80">{cur}</span></div>
          </div>
          <div className="card-flat p-4">
            <div className="text-[10px] uppercase text-[#94A3B8]">{t("jobs.activeJobs")}</div>
            <div className="font-display text-2xl font-extrabold mt-1">{activeCount}</div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="mt-10 text-sm text-[#94A3B8]">{t("jobs.loading")}</div>
      ) : jobs.length === 0 ? (
        <div className="mt-10 card-flat p-12 text-center" data-testid="jobs-empty">
          <Wallet size={36} className="mx-auto text-[#94A3B8]" />
          <p className="mt-4 font-semibold">{t("jobs.emptyTitle")}</p>
          <p className="text-sm text-[#94A3B8] mt-1">{t("jobs.emptyDesc")}</p>
        </div>
      ) : (
        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="jobs-list">
          {jobs.map((j) => {
            const tt = j.totals || {};
            const positive = (tt.profit || 0) >= 0;
            return (
              <Link key={j.id} to={`/app/lucrari/${j.id}`} data-testid={`job-card-${j.id}`}
                className="card-flat p-5 hover:border-[#00BFFF] transition-colors block">
                <div className="flex items-center justify-between">
                  <span className={`font-mono-plex text-xs px-2 py-0.5 ${j.status === "completed" ? "bg-[#22C55E] text-white" : "bg-[#1E293B] text-[#E2E8F0]"}`}>
                    {j.status === "completed" ? t("jobs.completed") : t("jobs.inProgress")}
                  </span>
                  <span className={`flex items-center gap-1 text-sm font-bold ${positive ? "text-[#22C55E]" : "text-[#EF4444]"}`}>
                    {positive ? <TrendingUp size={15} /> : <TrendingDown size={15} />} {(tt.margin_percent ?? 0)}%
                  </span>
                </div>
                <h3 className="font-display text-xl font-bold mt-3">{j.title}</h3>
                <p className="text-sm text-[#94A3B8]">{j.client_name || t("jobs.noClient")}</p>
                <div className="mt-4 grid grid-cols-3 gap-2 text-center">
                  <div><div className="text-[10px] uppercase text-[#94A3B8]">{t("jobs.value")}</div><div className="font-mono-plex text-sm">{(tt.value ?? 0).toFixed(0)}</div></div>
                  <div><div className="text-[10px] uppercase text-[#94A3B8]">{t("jobs.expenses")}</div><div className="font-mono-plex text-sm">{(tt.total_expenses ?? 0).toFixed(0)}</div></div>
                  <div><div className="text-[10px] uppercase text-[#94A3B8]">{t("jobs.profit")}</div><div className={`font-mono-plex text-sm font-bold ${positive ? "text-[#22C55E]" : "text-[#EF4444]"}`}>{(tt.profit ?? 0).toFixed(0)}</div></div>
                </div>
              </Link>
            );
          })}
        </div>
      )}

      {showNew && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="new-job-modal">
          <div className="bg-[#0B111D] w-full max-w-md p-6 relative">
            <button onClick={() => setShowNew(false)} className="absolute top-4 right-4" data-testid="close-new-job"><X size={20} /></button>
            <div className="overline">{t("jobs.newJob")}</div>
            <h2 className="font-display text-2xl font-extrabold mt-2 mb-4">{t("jobs.modalTitle")}</h2>
            <form onSubmit={create} className="space-y-3">
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-[#94A3B8]">{t("jobs.jobTitle")}</label>
                <input data-testid="job-title-input" className="input-flat mt-1" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder={t("jobs.jobTitlePh")} />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-[#94A3B8]">{t("jobs.clientOptional")}</label>
                <input data-testid="job-client-input" className="input-flat mt-1" value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-[#94A3B8]">{t("jobs.jobValue", { cur })}</label>
                <input data-testid="job-value-input" type="number" step="0.01" className="input-flat mt-1" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} placeholder={t("jobs.jobValuePh")} />
              </div>
              <button type="submit" disabled={saving} className="btn-primary w-full disabled:opacity-50 flex items-center justify-center gap-2" data-testid="create-job-submit">
                {saving ? <Loader2 size={18} className="animate-spin" /> : <Plus size={18} />} {t("jobs.createJob")}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
